import { Stack, Title } from '@mantine/core'
import { useTranslation } from 'react-i18next'

import { GenericTable, TableColumn } from '@/components/Table/GenericTable'
import { useTable } from '@/components/Table/useTable'
import { DistrictBreakdownRow } from './billingBreakdown'
import { formatCzk, formatKm, formatPercent } from './format'

export const BillingDistrictTable = ({
  rows,
  billingPeriodId,
}: {
  rows: DistrictBreakdownRow[]
  billingPeriodId: number
}) => {
  const { t, i18n } = useTranslation('billing')
  const locale = i18n.language
  const km = (item: DistrictBreakdownRow, key: keyof DistrictBreakdownRow) => formatKm(Number(item[key]), locale)

  const columns: TableColumn<DistrictBreakdownRow>[] = [
    { key: 'district_code', label: t('districts.district'), visible: true, sortable: true, align: 'left' },
    {
      key: 'billed_czk',
      label: t('districts.billed'),
      visible: true,
      sortable: true,
      align: 'right',
      type: 'number',
      render: item => formatCzk(item.billed_czk, locale),
      csvValue: item => item.billed_czk.toFixed(2),
    },
    {
      key: 'km_billed',
      label: t('districts.kmBilled'),
      visible: true,
      sortable: true,
      align: 'right',
      type: 'number',
      render: km,
      csvValue: item => item.km_billed.toFixed(2),
    },
    {
      key: 'km_ordered',
      label: t('districts.kmOrdered'),
      visible: true,
      sortable: true,
      align: 'right',
      type: 'number',
      render: km,
      csvValue: item => item.km_ordered.toFixed(2),
    },
    {
      key: 'fulfilment',
      label: t('districts.fulfilment'),
      visible: true,
      sortable: true,
      align: 'right',
      type: 'number',
      sortFunction: (a, b) => (a.fulfilment ?? -1) - (b.fulfilment ?? -1),
      render: item => (item.fulfilment === null ? '–' : formatPercent(item.fulfilment, locale)),
      csvValue: item => (item.fulfilment === null ? '' : item.fulfilment.toFixed(4)),
    },
    {
      key: 'unreliable_km_unbilled',
      label: t('districts.unreliableKm'),
      visible: true,
      sortable: true,
      align: 'right',
      type: 'number',
      render: km,
      csvValue: item => item.unreliable_km_unbilled.toFixed(2),
    },
    {
      key: 'km_missing_billable_overhead',
      label: t('districts.missingOverheadKm'),
      visible: false,
      sortable: true,
      align: 'right',
      type: 'number',
      render: km,
      csvValue: item => item.km_missing_billable_overhead.toFixed(2),
    },
    {
      key: 'km_potentially_billable_overhead',
      label: t('districts.potentialOverheadKm'),
      visible: false,
      sortable: true,
      align: 'right',
      type: 'number',
      render: km,
      csvValue: item => item.km_potentially_billable_overhead.toFixed(2),
    },
  ]

  const table = useTable({
    data: rows,
    columns,
    tableKey: 'billing-districts-table',
    defaultSortField: 'billed_czk',
    defaultSortDirection: 'desc',
    defaultFilterField: 'district_code',
    itemsPerPage: 25,
  })

  return (
    <Stack gap="sm">
      <Title order={3}>{t('districts.title')}</Title>
      <GenericTable
        {...table.tableProps}
        getRowKey={item => item.district_code}
        emptyMessage={t('districts.empty')}
        configTitle={t('districts.configure')}
        csvFileName={`billing-districts-${billingPeriodId}.csv`}
        horizontalSpacing="xs"
        verticalSpacing="xs"
      />
    </Stack>
  )
}

export default BillingDistrictTable
